import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail, Check } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import orellaLogo from '../../assets/logos/orella_46A8F9_logo.png'

/**
 * ForgotPasswordEmailScreen — "send me a link".
 *
 * ★★★ WHY THIS SCREEN EXISTS AT ALL. ForgotPasswordScreen beside it asks for a
 *   TELEPHONE NUMBER, and most patient accounts carry none — those people cannot
 *   even start a reset. A number that appears on more than one account now
 *   deliberately sends nothing. This screen is how both groups get back in.
 *   It does NOT replace the SMS screen: both channels are live, and retiring the
 *   SMS one is a separate decision with its own release date.
 *
 * ★★★ THE PROPERTY THIS SCREEN MUST NOT UNDO, AND IT IS THE WHOLE POINT OF THE
 *   SERVER ROUTE IT CALLS. "Is this person an Orella patient" is a fact about a
 *   named individual's care, and a stranger typing addresses into this box must
 *   learn NOTHING. So the server answers one 200 and one fixed sentence for a
 *   registered address, an unknown one, a deleted account, an unverified stub, an
 *   address inside its cooldown and a send that failed — and this screen renders
 *   ONE confirmation panel over all of them.
 *     · It does not say "no account with that address".
 *     · It does not disable or style the button differently for an address it
 *       does not recognise — it cannot, because it never learns.
 *     · It does not vary how long anything spins: there is one request, and the
 *       panel replaces the form the moment that request is accepted.
 *     · It logs nothing. Not the address, not the outcome.
 *   The only things that change what is rendered are the SHAPE of what was typed
 *   (checked here, before any request) and OUR OWN health (a thrown status).
 *   Neither can vary with the contents of the accounts table.
 *
 * ★★ AND THE 'retry' BRANCH IS NOT AN OVERSIGHT IN THAT RULE. Rendering the
 *   confirmation over a 503 would tell a locked-out patient a link is on its way
 *   when none was ever minted — the server refuses to do that and so does this.
 *   A known and an unknown address produce the same 503, so it distinguishes
 *   neither.
 */
export default function ForgotPasswordEmailScreen() {
  const navigate = useNavigate()
  const { requestPasswordResetEmail, loading } = useUserStore()
  const { t } = useI18n()

  const [email, setEmail] = useState('')
  const [error, setError] = useState(null)
  // ★ ONE flag, and it means "the request was accepted", never "an account was
  //   found". There is no state in this component that could hold the second
  //   fact, because this component is never told it.
  const [sent, setSent] = useState(false)

  useEffect(() => {
    setError(null)
  }, [])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // ★★ SHAPE ONLY, AND IT IS CHECKED HERE SO THE SERVER'S 400 IS UNREACHABLE.
    //   This is a fact about the characters typed — identical for every caller,
    //   knowable without consulting anything — which is why it is allowed to be
    //   a different outcome from the one below.
    const address = email.trim().toLowerCase()
    if (!address || !address.includes('@') || address.length > 254) {
      setError(t('auth.invalidEmail'))
      return
    }

    const result = await requestPasswordResetEmail(address)

    // ★★★ THE ONLY BRANCH, AND IT IS ON OUR OWN HEALTH. `result` carries no field
    //   that could tell a registered address from an unknown one, because the
    //   store never reads the response body — see requestPasswordResetEmail.
    if (result.outcome === 'sent') {
      setSent(true)
    } else {
      setError(t('errors2.somethingWentWrong'))
    }
  }

  if (sent) {
    return (
      <ResponsiveWrapper className="bg-white" bottomInset={false}>
        <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
          <div className="flex items-center gap-4 mb-6">
            <button
              onClick={() => navigate('/signin')}
              className="p-2 -ml-2 text-gray-600 hover:text-gray-800"
              aria-label={t('common.back')}
            >
              <ArrowLeft size={24} />
            </button>
            <img src={orellaLogo} alt="Orella" className="h-8 md:h-10 object-contain" />
          </div>

          <div className="flex flex-col items-center text-center pt-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            {/* ★★★ THE SENTENCE IS THE SAME ONE THE SERVER RETURNS, AND IT IS
                DELIBERATELY CONDITIONAL. "If an Orella account exists" is not
                hedging — it is the most this screen is permitted to know. */}
            <p className="text-gray-600 md:text-lg mb-8">
              {t('auth.resetLinkSentGeneric')}
            </p>
            {/* ★★ "I never got the mail" needs a way forward, not a diagnosis.
                Nothing here can say whether a mail was sent, so the honest help
                is: look in spam, then ask for another one. */}
            <p className="text-sm text-gray-500 mb-8">
              {t('auth.resetLinkNotReceived')}
            </p>
          </div>
          {/* PROD-1194: the two actions are pinned at the bottom of the screen. */}
          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            <button
              onClick={() => { setSent(false); setError(null) }}
              className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full transition-opacity breathe-blue"
            >
              {t('auth.requestNewLink')}
            </button>
            <button
              onClick={() => navigate('/signin')}
              className="w-full py-4 mt-3 bg-gray-200 text-gray-600 font-semibold rounded-full"
            >
              {t('auth.signIn')}
            </button>
          </BottomActionBar>
        </div>
      </ResponsiveWrapper>
    )
  }

  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
      <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/signin')}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-800"
            aria-label={t('common.back')}
          >
            <ArrowLeft size={24} />
          </button>
          <img src={orellaLogo} alt="Orella" className="h-8 md:h-10 object-contain" />
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-orella-500 mb-2">
          {t('auth.forgotPasswordTitle')}
        </h1>
        <p className="text-gray-500 mb-8 md:text-lg">
          {t('auth.forgotPasswordEmailDesc')}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {error}
          </div>
        )}

        {/* PROD-1194: the form fills the column so Send sits pinned at the bottom of the screen. */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div>
            <label className="block text-sm text-gray-500 mb-1" htmlFor="reset-email">
              {t('auth.email')}
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                id="reset-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500"
                placeholder={t('auth.emailPlaceholder')}
                autoComplete="email"
                required
              />
            </div>
          </div>

          {/* ★★★ THE BUTTON IS NEVER DISABLED ON ACCOUNT OF THE ADDRESS. Only on
              `loading`, which is a fact about the request in flight. A button
              that greys out for an unrecognised address is the same oracle as a
              404, drawn instead of written. */}
          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 transition-opacity breathe-blue"
            >
              {loading ? t('common.sending') : t('auth.sendResetLink')}
            </button>
          </BottomActionBar>
        </form>

        <p className="text-center text-gray-500 mt-4 bottom-actions [--ba-min:32px]">
          {t('auth.rememberPassword')}{' '}
          <button onClick={() => navigate('/signin')} className="text-orella-500 font-semibold">
            {t('auth.signIn')}
          </button>
        </p>
      </div>
    </ResponsiveWrapper>
  )
}
